import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import usePartDefaultCategories from "../../../data/usePartDefaultCategories";
import useShipping from "../../../data/useShipping";
import { IShippingAddress, IShippingDimensions, IShippingQuote } from "../../../models/Shipping";
import { IPart } from "../../../models/Part";
import { resolveAddress, resolveDimensions } from "../../ebayOrders/CreateShippingLabelsModal";
import { FedexShipping } from "./FedexShipping";
import { ProoviaShipping } from "./ProoviaShipping";
import LoadingTable from "../../shared/LoadingTable";
import { IShippingProvider } from "../../../models/ShippingProvider";
import { ShippingQuoteTable } from "./ShippingQuoteTable";

export default function ShippingProviders({ 
  part, 
  providerId,
  selectedProvider,
  shippingService,
  setShippingService,
  setProoviaItemId,
  setProoviaCollection,
  setProoviaDelivery
}: { 
  part: IPart, 
  providerId: string | undefined,
  selectedProvider: IShippingProvider | undefined,
  shippingService: string | undefined, 
  setShippingService: (value: string | undefined) => void ,
  setProoviaItemId: (value: number) => void,
  setProoviaCollection: (value: string) => void,
  setProoviaDelivery: (value: string) => void
}){
  const { t } = useTranslation("stock");
  const { shippingInfo, quotes } = useShipping(part);
  const { defaultPartCategory } = usePartDefaultCategories({ part });

  // -------- Address -------- //
  const [address, setAddress] = useState<IShippingAddress>();
  const [addressLoading, setAddressLoading] = useState(true);

  useEffect(() => {
    if (!shippingInfo.isLoading && shippingInfo.data) {
      setAddress(resolveAddress(part, shippingInfo));
      setAddressLoading(false);
    }
  }, [shippingInfo.isLoading, shippingInfo.data, part]);

  // -------- Dimensions -------- //
  const [dimensions, setDimensions] = useState<IShippingDimensions>();
  const [dimensionsLoading, setDimensionsLoading] = useState(true);
  const [genericQuotesLoading, setGenericQuotesLoading] = useState(false);
  const [genericQuotes, setGenericQuotes] = useState<IShippingQuote[]>([]);
  const [genericQuoteError, setGenericQuoteError] = useState<string>();

  useEffect(() => {
    if (!defaultPartCategory.isLoading && defaultPartCategory.data) {
      setDimensions(resolveDimensions(part, defaultPartCategory));
      setDimensionsLoading(false);
    }

    if (!part.categoryId) {
      setDimensionsLoading(false);
    }
  }, [defaultPartCategory.isLoading, defaultPartCategory.data, part]);

  // Callers may pass inline callbacks (e.g. the bulk label modal wires these to
  // react-hook-form setValue), so keep them out of the effect deps - a new
  // function identity each render would re-fire the quote request forever.
  const setShippingServiceRef = useRef(setShippingService);
  setShippingServiceRef.current = setShippingService;

  const shippingServiceRef = useRef(shippingService);
  shippingServiceRef.current = shippingService;

  useEffect(() => {
    if (!providerId || providerId === "FEDEX" || providerId === "PROOVIA") return;
    if (!address || !dimensions) return;

    setGenericQuotesLoading(true);
    setGenericQuoteError(undefined);
    quotes.mutate(
      {
        ...address,
        ...dimensions,
        partDataId: part.id,
        providerId
      },
      {
        onSuccess: (res) => {
          const quoteRows = res.data ?? [];
          setGenericQuotes(quoteRows);

          const selectedStillValid = quoteRows.some((q) => q.service === shippingServiceRef.current && (q.isValid ?? true));
          if (!selectedStillValid && shippingServiceRef.current !== undefined) {
            setShippingServiceRef.current(undefined);
          }
        },
        onError: (error: any) => {
          setGenericQuotes([]);
          if (shippingServiceRef.current !== undefined) setShippingServiceRef.current(undefined);
          setGenericQuoteError(error?.message || t("Unable to load available services."));
        },
        onSettled: () => {
          setGenericQuotesLoading(false);
        }
      }
    );
  }, [providerId, address, dimensions, part.id]);


  if (addressLoading || dimensionsLoading) return <LoadingTable headers={[t("Service"), t("Price")]} rows={3} />;

  return (
    <>
      {providerId === "FEDEX" && (
        <FedexShipping
          address={address!}
          dimensions={dimensions!}
          part={part}
          onServiceSelect={(svc: any) => setShippingService(svc)}
          selectedService={shippingService}
        />
      )}
      {providerId === "PROOVIA" && (
        <ProoviaShipping
          address={address!}
          dimensions={dimensions!}
          part={part}
          onProoviaItemSelect={(item) => setProoviaItemId(item)}
          onCollectionSlotSelect={(slot) =>
            setProoviaCollection(slot)
          }
          onDeliverySlotSelect={(slot) =>
            setProoviaDelivery(slot)
          }
          onServiceSelect={(svc) => setShippingService(svc)}
          selectedService={shippingService}
        />
      )}
      {providerId !== "FEDEX" && providerId !== "PROOVIA" && (
        <div className="space-y-2">
          <p className="text-sm text-gray-500">{t("Select a service for {{provider}}", { provider: selectedProvider?.providerName ?? providerId })}</p>
          {genericQuoteError ? (
            <div className="bg-red-100 text-red-600 px-4 py-2 rounded text-sm">{genericQuoteError}</div>
          ) : genericQuotesLoading ? (
            <LoadingTable headers={[t("Service"), t("Price")]} rows={3} />
          ) : (
            <ShippingQuoteTable
              shippingQuotes={genericQuotes}
              onServiceSelect={(svc) => setShippingService(svc)}
              selectedService={shippingService}
            />
          )}
        </div>
      )}
    </>
  )
}
